import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Workflow, Plus, Zap, PlayCircle, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const TRIGGER_LABELS = {
  contact_created: "Contact created",
  tag_added: "Tag added",
  stage_changed: "Pipeline stage changed",
  form_submitted: "Form submitted",
};

export default function Automations() {
  const { activeSubId } = useAuth();
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!activeSubId) return;
    setLoading(true);
    api.get(`/automations?sub_account_id=${activeSubId}`).then(({ data }) => { setWorkflows(data); setLoading(false); });
  }, [activeSubId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (wf, enabled) => {
    setWorkflows((ws) => ws.map((w) => (w.id === wf.id ? { ...w, enabled } : w)));
    await api.put(`/automations/${wf.id}`, { enabled });
    toast.success(enabled ? "Automation enabled" : "Automation paused");
  };

  const create = async () => {
    const { data } = await api.post(`/automations?sub_account_id=${activeSubId}`, {
      name: "Untitled automation", trigger_type: "contact_created", trigger_config: {}, steps: [], enabled: false,
    });
    navigate(`/automations/${data.id}`);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Connective Tissue</p>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">Automations</h1>
        </div>
        <Button onClick={create} className="gap-2 font-semibold" data-testid="new-automation-button"><Plus className="w-4 h-4" />New automation</Button>
      </div>

      {workflows.length === 0 ? (
        <Card className="p-16 text-center bg-card border-border/70" data-testid="automations-empty">
          <Workflow className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No automations yet. Build your first trigger → action workflow.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {workflows.map((wf) => (
            <Card key={wf.id} className="p-6 bg-card border-border/70 hover:border-primary/40 transition-colors group" data-testid={`automation-card-${wf.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0" onClick={() => navigate(`/automations/${wf.id}`)} role="button">
                  <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-display font-semibold truncate group-hover:text-primary transition-colors">{wf.name}</p>
                    <p className="text-xs text-muted-foreground">{wf.steps?.length || 0} steps</p>
                  </div>
                </div>
                <Switch checked={wf.enabled} onCheckedChange={(v) => toggle(wf, v)} data-testid={`automation-toggle-${wf.id}`} />
              </div>
              <div className="flex items-center gap-2 mt-5 text-xs flex-wrap">
                <Badge variant="secondary" className="gap-1"><PlayCircle className="w-3 h-3" />{TRIGGER_LABELS[wf.trigger_type]}</Badge>
                <ArrowRight className="w-3 h-3 text-muted-foreground" />
                <span className="text-muted-foreground">{wf.steps?.length || 0} action{wf.steps?.length === 1 ? "" : "s"}</span>
                <span className="ml-auto font-mono text-muted-foreground">{wf.entered ?? 0} in · {wf.active ?? 0} active</span>
              </div>
              <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => navigate(`/automations/${wf.id}`)} data-testid={`edit-automation-${wf.id}`}>
                Open builder
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
